
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  ImageIcon,
  X,
  ExternalLink,
} from 'lucide-react';

import RelatedWorks from './RelatedWorks';
import '../pages/ActiveDeterrenceCamera.css';
import './CategoryTemplate.css';

const CategoryTemplate = ({
  id,
  eyebrow,
  name,
  description,
  heroImage,
  trustPoints = [],
  types = [],
  featuredProducts = [],
  processSteps = [],
  samples = [],
  showSamples = true,
  worksCategory = null,
  ctaTitle,
  ctaBody,
  relatedServices = [],
  backTo = '/products',
  backLabel = '製品・サービス一覧へ戻る',
}) => {
  const [activeCatalogue, setActiveCatalogue] = useState(null);

  useEffect(() => {
    if (!activeCatalogue) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveCatalogue(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeCatalogue]);

  return (
    <div
      className="adc-page"
      data-testid={`category-page-${id}`}
    >
      {/* ===== Hero ===== */}
      <section
        className="adc-hero cat-hero"
        data-testid="category-hero"
      >
        <div className="container">
          <div className="adc-hero-inner">
            <div className="adc-hero-body">
              <Link
                to={backTo}
                className="adc-back-link"
                data-testid="back-link-top"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                {backLabel}
              </Link>

              <span className="adc-hero-eyebrow">
                <span
                  className="adc-hero-eyebrow-dot"
                  aria-hidden="true"
                />
                {eyebrow}
              </span>

              <h1 className="adc-hero-title">{name}</h1>

              <span
                className="adc-hero-divider"
                aria-hidden="true"
              />

              <p className="adc-hero-lead">{description}</p>

              {trustPoints.length > 0 && (
                <ul
                  className="cat-hero-points"
                  data-testid="cat-hero-points"
                >
                  {trustPoints.map((p, i) => {
                    const Icon = p.Icon;

                    return (
                      <li
                        key={i}
                        className="cat-hero-point"
                        data-testid={`trust-point-${i}`}
                      >
                        <span className="cat-hero-point-icon">
                          {Icon ? (
                            <Icon size={16} strokeWidth={1.9} />
                          ) : null}
                        </span>

                        <span className="cat-hero-point-label">
                          {p.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div
              className="adc-hero-photo"
              data-testid="hero-photo"
            >
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={`${name} イメージ`}
                />
              ) : (
                <div className="adc-hero-photo-placeholder">
                  IMAGE
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Products Lineup ===== */}
      {types.length > 0 && (
        <section
          className="adc-section"
          id="lineup"
          data-testid="section-lineup"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">LINEUP</span>
              <h2 className="adc-title">取扱ラインナップ</h2>
            </div>

            <div
              className="cat-lineup-grid"
              data-testid="cat-lineup-grid"
            >
              {types.map((t, i) => (
                <article
                  key={i}
                  className="cat-lineup-card"
                  data-testid={`lineup-card-${i}`}
                >
                  <div
                    className="cat-lineup-image"
                    style={
                      t.image
                        ? {
                            backgroundImage: `url(${t.image})`,
                          }
                        : undefined
                    }
                  >
                    {!t.image && (
                      <div className="cat-lineup-image-fallback">
                        <ImageIcon
                          size={22}
                          strokeWidth={1.4}
                        />
                        <span>準備中</span>
                      </div>
                    )}
                  </div>

                  <div className="cat-lineup-body">
                    <h3 className="cat-lineup-title">
                      {t.title}
                    </h3>

                    {t.desc && (
                      <p className="cat-lineup-desc">
                        {t.desc}
                      </p>
                    )}

                    {t.to && (
                      <Link
                        to={t.to}
                        className="cat-lineup-detail-btn"
                        data-testid={`lineup-detail-${i}`}
                      >
                        製品詳細を見る
                        <ArrowRight
                          size={15}
                          strokeWidth={2}
                        />
                      </Link>
                    )}

                    {t.file && (
                      <a
                        href={t.file}
                        className="cat-lineup-detail-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lineup-file-${i}`}
                      >
                        資料を見る
                        <ArrowRight
                          size={15}
                          strokeWidth={2}
                        />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Featured Product Series ===== */}
      {featuredProducts.length > 0 && (
        <section
          className="adc-section adc-section-alt cat-featured-section"
          id="featured-products"
          data-testid="section-featured-products"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                PRODUCT SERIES
              </span>
              <h2 className="adc-title">
                取扱製品・製品シリーズ
              </h2>
            </div>

            <div className="cat-featured-grid">
              {featuredProducts.map((product, i) => (
                <article
                  className="cat-featured-card"
                  key={product.name || i}
                >
                  <div className="cat-featured-image">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={`${product.name} 製品画像`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="cat-lineup-image-fallback">
                        <ImageIcon
                          size={22}
                          strokeWidth={1.4}
                        />
                        <span>製品画像準備中</span>
                      </div>
                    )}
                  </div>

                  <div className="cat-featured-body">
                    <h3 className="cat-featured-title">
                      {product.name}
                    </h3>

                    {product.subtitle && (
                      <p className="cat-featured-subtitle">
                        {product.subtitle}
                      </p>
                    )}

                    {product.description && (
                      <p className="cat-featured-desc">
                        {product.description}
                      </p>
                    )}

                    {product.file ? (
                      <button
                        type="button"
                        className="cat-lineup-detail-btn"
                        onClick={() =>
                          setActiveCatalogue({
                            name: product.name,
                            file: product.file,
                          })
                        }
                        aria-label={`${product.name}のカタログを見る`}
                      >
                        カタログを見る
                        <ArrowRight
                          size={15}
                          strokeWidth={2}
                        />
                      </button>
                    ) : product.to ? (
                      <Link
                        className="cat-lineup-detail-btn"
                        to={product.to}
                      >
                        製品詳細を見る
                        <ArrowRight
                          size={15}
                          strokeWidth={2}
                        />
                      </Link>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Process ===== */}
      {processSteps.length > 0 && (
        <section
          className="adc-section adc-section-alt"
          id="process"
          data-testid="section-process"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">PROCESS</span>
              <h2 className="adc-title">
                ご相談から施工までの流れ
              </h2>
            </div>

            <ol
              className="cat-process-list"
              data-testid="cat-process-list"
            >
              {processSteps.map((s, i) => (
                <li
                  key={i}
                  className="cat-process-item"
                  data-testid={`process-step-${i}`}
                >
                  <span className="cat-process-num">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="cat-process-body">
                    <h3 className="cat-process-title">
                      {s.title}
                    </h3>
                    <p className="cat-process-desc">
                      {s.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ===== Actual HS Project Works ===== */}
      {worksCategory && (
        <RelatedWorks
          category={worksCategory}
          title={`${name}の施工実績`}
          initialLimit={3}
        />
      )}

      {/* ===== Original Sample Work (fallback) ===== */}
      {!worksCategory && showSamples && (
        <section
          className="adc-section"
          id="samples"
          data-testid="section-samples"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">
                SAMPLE WORK
              </span>
              <h2 className="adc-title">施工事例</h2>
            </div>

            {samples.length > 0 ? (
              <div
                className="cat-samples-grid"
                data-testid="cat-samples-grid"
              >
                {samples.map((s, i) => (
                  <article
                    key={i}
                    className="cat-sample-card"
                    data-testid={`sample-card-${i}`}
                  >
                    <div
                      className="cat-sample-image"
                      style={
                        s.image
                          ? {
                              backgroundImage: `url(${s.image})`,
                            }
                          : undefined
                      }
                    >
                      {!s.image && (
                        <div className="cat-sample-image-fallback">
                          <ImageIcon
                            size={20}
                            strokeWidth={1.4}
                          />
                        </div>
                      )}

                      {s.tag && (
                        <span className="cat-sample-tag">
                          {s.tag}
                        </span>
                      )}
                    </div>

                    <div className="cat-sample-body">
                      <h3 className="cat-sample-title">
                        {s.title}
                      </h3>

                      {s.location && (
                        <p className="cat-sample-meta">
                          {s.location}
                        </p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div
                className="cat-samples-empty"
                data-testid="cat-samples-empty"
              >
                <div className="cat-samples-empty-grid">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="cat-sample-placeholder"
                    >
                      <ImageIcon
                        size={22}
                        strokeWidth={1.3}
                      />
                      <span>施工事例は準備中です</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== Related Services ===== */}
      {relatedServices.length > 0 && (
        <section
          className="adc-section adc-section-alt"
          id="related"
          data-testid="section-related"
        >
          <div className="container">
            <div className="adc-section-head">
              <span className="adc-eyebrow">RELATED</span>
              <h2 className="adc-title">関連サービス</h2>
            </div>

            <div
              className="cat-related-grid"
              data-testid="cat-related-grid"
            >
              {relatedServices.map((r, i) => (
                <Link
                  key={i}
                  to={r.to}
                  className="cat-related-card"
                  data-testid={`related-card-${i}`}
                >
                  <div
                    className="cat-related-image"
                    style={
                      r.image
                        ? {
                            backgroundImage: `url(${r.image})`,
                          }
                        : undefined
                    }
                  >
                    {!r.image && (
                      <span className="cat-related-image-fallback" />
                    )}

                    {r.label && (
                      <span className="cat-related-label">
                        {r.label}
                      </span>
                    )}
                  </div>

                  <div className="cat-related-body">
                    <h3 className="cat-related-title">
                      {r.name}
                    </h3>

                    {r.desc && (
                      <p className="cat-related-desc">
                        {r.desc}
                      </p>
                    )}

                    <span className="cat-related-cta">
                      詳細を見る
                      <ArrowRight
                        size={14}
                        strokeWidth={2}
                      />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Contact CTA ===== */}
      <section
        className="adc-cta"
        data-testid="section-cta"
      >
        <div className="container">
          <div className="adc-cta-card">
            <span className="adc-cta-eyebrow">
              CONTACT
            </span>

            <h2 className="adc-cta-title">
              {ctaTitle ||
                `${name}についてご相談ください`}
            </h2>

            <p className="adc-cta-body">
              {ctaBody ||
                '設置場所やご要望に合わせて、最適な内容をご提案いたします。お気軽にご相談ください。'}
            </p>

            <Link
              to="/contact"
              className="adc-cta-btn"
              data-testid="cta-btn"
            >
              このサービスについて相談する
              <ArrowRight
                size={18}
                strokeWidth={2}
              />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Catalogue PDF Popup ===== */}
      {activeCatalogue && (
        <div
          className="cat-catalog-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setActiveCatalogue(null);
            }
          }}
        >
          <div
            className="cat-catalog-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`${activeCatalogue.name} 製品カタログ`}
          >
            <div className="cat-catalog-toolbar">
              <strong>
                {activeCatalogue.name}｜製品カタログ
              </strong>

              <div className="cat-catalog-actions">
                <a
                  href={activeCatalogue.file}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={16} />
                  別タブで開く
                </a>

                <button
                  type="button"
                  onClick={() => setActiveCatalogue(null)}
                  aria-label="閉じる"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <iframe
              src={`${activeCatalogue.file}#view=FitH`}
              title={`${activeCatalogue.name} カタログ`}
            />

            <p className="cat-catalog-fallback">
              PDFが表示されない場合は「別タブで開く」をご利用ください。
            </p>
          </div>
        </div>
      )}

      {/* ===== Bottom Back Link ===== */}
      <section
        className="adc-bottom-nav"
        data-testid="section-bottom-nav"
      >
        <div className="container">
          <Link
            to={backTo}
            className="adc-bottom-back"
            data-testid="back-link-bottom"
          >
            <ArrowLeft
              size={16}
              strokeWidth={2}
            />
            {backLabel}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CategoryTemplate;
